import { Injectable, OnDestroy } from "@angular/core";
import { Banner, BannerActionType, SubscriptionTier } from "@types";
import {
  generateUpgradeUrl,
  isNotNullOrUndefined,
  isNumber,
  sorted,
  SubscriptionContainer,
} from "@utils";
import {
  BehaviorSubject,
  filter,
  first,
  forkJoin,
  map,
  merge,
  Subject,
  switchMap,
  tap,
  throttleTime,
  toArray,
} from "rxjs";
import { AccountService } from "./account.service";
import { ApiService } from "./api.service";
import { AuthService } from "./auth.service";
import { CompaniesService } from "./companies.service";

const millisecondsInADay = 86400000;
const dismissUpgradeBannerDurationInDays = 30;
const dismissUpgradeBannerDurationInMilliseconds =
  millisecondsInADay * dismissUpgradeBannerDurationInDays;

const upgradeBannerId = "upgradeBanner";

type UpgradeBanner = Banner & {
  _type: "upgradeBanner";
};

const isUpgradeBanner = (value: Banner): value is UpgradeBanner => {
  return (value as UpgradeBanner)._type === "upgradeBanner";
};

@Injectable({
  providedIn: "root",
})
export class BannerService implements OnDestroy {
  private bannersSubject = new BehaviorSubject<Banner[]>([]);
  public banners$ = this.bannersSubject.asObservable();

  /** `true` if there are no banners to display, meaning the entire banner UI is dismissed. */
  public isBannerDismissed$ = this.bannersSubject.pipe(
    map((s) => s.length === 0)
  );

  // This is to support elements that are displayed regardless of whether the upgrade banner has been dismissed
  private isAllowedToDisplaySubject = new BehaviorSubject<boolean>(false);
  public isAllowedToDisplay$ = this.isAllowedToDisplaySubject.asObservable();

  private getBannersSubject = new Subject<void>();
  private getBanners$ = this.getBannersSubject.pipe(
    throttleTime(millisecondsInADay)
  );

  // Banners added via addBanner() (rather than fetched from the API), kept so they survive getBanners() refreshes.
  private manualBanners: Banner[] = [];

  private subscriptions = new SubscriptionContainer();

  public constructor(
    private apiService: ApiService,
    private companiesService: CompaniesService,
    private authService: AuthService,
    private accountService: AccountService
  ) {
    const authenticatedSubscription = merge(
      this.authService.isAuthenticated$.pipe(
        filter((isAuthenticated) => isAuthenticated)
      ),
      this.getBanners$
    )
      .pipe(
        switchMap(() => {
          return this.getBanners();
        })
      )
      .subscribe();

    this.subscriptions.add(authenticatedSubscription);
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public setIsAllowedToDisplay(isAllowedToDisplay: boolean) {
    this.isAllowedToDisplaySubject.next(isAllowedToDisplay);
  }

  public updateIsBannerDismissed() {
    const isDismissed = this.isBannerDismissed(
      upgradeBannerId,
      dismissUpgradeBannerDurationInMilliseconds
    );
    if (!isDismissed) {
      const bannerSubscription = forkJoin({
        pages: this.companiesService
          .getCompaniesV1({ fetchAll: true })
          .pipe(toArray()),
        account: this.accountService.userAccount$.pipe(
          first(isNotNullOrUndefined)
        ),
      }).subscribe({
        next: ({ pages, account }) => {
          const companies = pages
            .flatMap((page) => page.data)
            .filter(isNotNullOrUndefined);

          const isDismissedWhileFetching = this.isBannerDismissed(
            upgradeBannerId,
            dismissUpgradeBannerDurationInMilliseconds
          );

          const isPaidUser = companies.some(
            (c) => c.plan !== SubscriptionTier.Free
          );

          const isNzUser = account.countryCode?.toLowerCase() === "nz";

          const isUpgradeBannerDismissed =
            isDismissedWhileFetching || isPaidUser || isNzUser;

          if (!isUpgradeBannerDismissed) {
            const upgradeBanner: UpgradeBanner = {
              _type: "upgradeBanner",
              id: upgradeBannerId,
              content:
                "Updated Free Plan is now live. Start a free trial of a paid plan - no payment or card required. Already collaborating with a team in a paid workspace? Ask their workspace admin to invite you.",
              order: Number.MAX_SAFE_INTEGER,
              actions: [
                {
                  displayText: "Upgrade",
                  type: BannerActionType.Link,
                  value: generateUpgradeUrl(),
                  order: 0,
                },
              ],
              category: 1,
              isDismissible: true,
            };

            this.bannersSubject.next(
              sorted(
                [...this.bannersSubject.value, upgradeBanner],
                (a, b) => a.order - b.order
              )
            );
          }
        },
      });

      this.subscriptions.add(bannerSubscription);
    }
  }

  private isBannerDismissed(
    bannerId: string,
    dismissDuration: number,
    storage: Storage = localStorage
  ) {
    const key = this.createBannerStorageKey(bannerId);
    const dismissedAt = storage.getItem(key);

    if (dismissedAt === null) return false;
    if (dismissDuration < 0) return true;

    let dismissedUntilTime = Number(dismissedAt);
    if (!isNumber(dismissedUntilTime)) return false;
    dismissedUntilTime += dismissDuration;

    return Date.now() < dismissedUntilTime;
  }

  private getBannerDismissalStorage(banner: Pick<Banner, "dismissPersistence">) {
    return banner.dismissPersistence === "session" ? sessionStorage : localStorage;
  }

  private createBannerStorageKey(bannerId: string) {
    return `dismissed-${bannerId}-at`;
  }

  public dismissBanner(banner: Banner) {
    if (banner.dismissPersistence !== "none") {
      const key = this.createBannerStorageKey(banner.id);
      this.getBannerDismissalStorage(banner).setItem(key, `${Date.now()}`);
    }
    this.manualBanners = this.manualBanners.filter((b) => b.id !== banner.id);
    this.bannersSubject.next(
      this.bannersSubject.value.filter((b) => b.id !== banner.id)
    );
  }

  /**
   * Adds (or replaces, if the id already exists) a banner without going through the `/banners` API.
   * No-ops if the banner was already dismissed (per its `dismissDuration`).
   */
  public addBanner(banner: Banner) {
    if (
      banner.dismissPersistence !== "none" &&
      this.isBannerDismissed(banner.id, -1, this.getBannerDismissalStorage(banner))
    ) {
      return;
    }

    this.manualBanners = [
      ...this.manualBanners.filter((b) => b.id !== banner.id),
      banner,
    ];
    this.bannersSubject.next(
      sorted(
        [
          ...this.bannersSubject.value.filter((b) => b.id !== banner.id),
          banner,
        ],
        (a, b) => a.order - b.order
      )
    );
  }

  public refreshBanners() {
    this.getBannersSubject.next();
  }

  private getBanners() {
    return this.apiService
      .get<Banner[]>({
        path: "/api/v3/user/banners",
        queryParams: {
          platform: "web",
        },
      })
      .pipe(
        tap({
          next: (banners) => {
            const upgradeBanner = this.bannersSubject.value.find((b) =>
              isUpgradeBanner(b)
            );

            const notDismissedBanners = banners.filter(
              (b) =>
                !b.isDismissible ||
                !this.isBannerDismissed(
                  b.id,
                  -1,
                  this.getBannerDismissalStorage(b)
                )
            );

            const preservedManualBanners = this.manualBanners.filter(
              (b) => !notDismissedBanners.some((nb) => nb.id === b.id)
            );

            const sortedBanners = sorted(
              [...notDismissedBanners, ...preservedManualBanners],
              (a, b) => a.order - b.order
            );
            if (upgradeBanner) {
              sortedBanners.push(upgradeBanner);
            }

            this.bannersSubject.next(sortedBanners);
          },
        })
      );
  }
}
